import { useEffect, useRef, useState } from 'react';
import {
  PULL_TO_RELOAD_THRESHOLD_PX,
  canStartPullFromTarget,
  isPwaPullToReloadEnabled,
  reloadApp,
  resistedPullDistance,
  subscribeToPullToReloadEnabled,
} from '../utils/pwaPullToReload';

const DIRECTION_LOCK_PX = 6;
const CAPTURE_PASSIVE = { capture: true, passive: true };
const CAPTURE_ACTIVE = { capture: true, passive: false };

export const usePwaPullToReloadEnabled = () => {
  const [enabled, setEnabled] = useState(() => isPwaPullToReloadEnabled());

  useEffect(() => {
    const sync = () => setEnabled(isPwaPullToReloadEnabled());
    sync();
    return subscribeToPullToReloadEnabled(sync);
  }, []);

  return enabled;
};

const usePwaPullToReload = ({
  enabled = false,
  onReload = reloadApp,
} = {}) => {
  const [pullDistance, setPullDistance] = useState(0);
  const [reloading, setReloading] = useState(false);
  const gestureRef = useRef(null);
  const pullDistanceRef = useRef(0);
  const reloadingRef = useRef(false);
  const rafRef = useRef(null);

  useEffect(() => {
    if (!enabled || typeof window === 'undefined') {
      return undefined;
    }

    const setPull = (value) => {
      pullDistanceRef.current = value;
      if (rafRef.current != null) return;
      rafRef.current = window.requestAnimationFrame(() => {
        rafRef.current = null;
        setPullDistance(pullDistanceRef.current);
      });
    };

    const onTouchStart = (event) => {
      if (reloadingRef.current || event.touches.length !== 1) return;
      const touch = event.touches[0];
      if (!canStartPullFromTarget(event.target)) {
        gestureRef.current = null;
        return;
      }

      gestureRef.current = {
        startX: touch.clientX,
        startY: touch.clientY,
        pulling: false,
      };
    };

    const onTouchMove = (event) => {
      const gesture = gestureRef.current;
      if (!gesture || reloadingRef.current || event.touches.length !== 1) return;

      const touch = event.touches[0];
      const deltaX = touch.clientX - gesture.startX;
      const deltaY = touch.clientY - gesture.startY;

      if (!gesture.pulling) {
        if (Math.abs(deltaY) < DIRECTION_LOCK_PX && Math.abs(deltaX) < DIRECTION_LOCK_PX) {
          return;
        }
        if (deltaY <= 0 || Math.abs(deltaX) > deltaY) {
          gestureRef.current = null;
          setPull(0);
          return;
        }
        gesture.pulling = true;
      }

      if (event.cancelable) {
        event.preventDefault();
      }
      setPull(resistedPullDistance(deltaY));
    };

    const finishGesture = () => {
      const gesture = gestureRef.current;
      const distance = pullDistanceRef.current;
      gestureRef.current = null;

      if (!gesture?.pulling) {
        setPull(0);
        return;
      }

      if (distance >= PULL_TO_RELOAD_THRESHOLD_PX) {
        reloadingRef.current = true;
        setReloading(true);
        setPull(PULL_TO_RELOAD_THRESHOLD_PX);
        onReload();
        return;
      }

      setPull(0);
    };

    const listenerTarget = document;
    listenerTarget.addEventListener('touchstart', onTouchStart, CAPTURE_PASSIVE);
    listenerTarget.addEventListener('touchmove', onTouchMove, CAPTURE_ACTIVE);
    listenerTarget.addEventListener('touchend', finishGesture, CAPTURE_PASSIVE);
    listenerTarget.addEventListener('touchcancel', finishGesture, CAPTURE_PASSIVE);

    return () => {
      listenerTarget.removeEventListener('touchstart', onTouchStart, CAPTURE_PASSIVE);
      listenerTarget.removeEventListener('touchmove', onTouchMove, CAPTURE_ACTIVE);
      listenerTarget.removeEventListener('touchend', finishGesture, CAPTURE_PASSIVE);
      listenerTarget.removeEventListener('touchcancel', finishGesture, CAPTURE_PASSIVE);
      if (rafRef.current != null) {
        window.cancelAnimationFrame(rafRef.current);
      }
    };
  }, [enabled, onReload]);

  return {
    pullDistance,
    reloading,
    armed: pullDistance >= PULL_TO_RELOAD_THRESHOLD_PX || reloading,
  };
};

export default usePwaPullToReload;
